import {
  buildLectureNumberMap,
  findCourseCategory,
  sortLectureItems,
} from "./course-categories";
import { getLearningScore, isCoursePassingScore } from "./learning-score";
import type { ScoreRow } from "./scores-api";
import type { StudentScenario } from "./student-course-progress";

export function summarizeCourseAttempts(rows: ScoreRow[]) {
  const attempts = [...rows].sort((a, b) => {
    const timestamp = (value: string) =>
      Number.isFinite(Date.parse(value)) ? Date.parse(value) : 0;
    return (
      timestamp(a.created) - timestamp(b.created) ||
      a.interview_id - b.interview_id
    );
  });
  const full = attempts.filter(
    (row) =>
      typeof row.patient_score_value === "number" &&
      Number.isFinite(row.patient_score_value) &&
      getLearningScore(row)?.max === 100,
  );
  const best = full.length
    ? Math.max(...full.map((row) => getLearningScore(row)!.score))
    : null;
  const delta =
    full.length > 1
      ? getLearningScore(full[full.length - 1])!.score -
        getLearningScore(full[0])!.score
      : null;
  return {
    attempts,
    full,
    best,
    delta,
    latest: attempts[attempts.length - 1],
    completed: attempts.some(isCoursePassingScore),
  };
}

export function buildCourseScoreHistory(
  scenarios: StudentScenario[],
  rows: ScoreRow[],
) {
  const grouped = new Map<number, ScoreRow[]>();
  const available = new Set(scenarios.map((s) => s.id));
  const unassigned: ScoreRow[] = [];
  // Multiple pages may overlap if another score arrives during pagination.
  const unique = new Map<number, ScoreRow>();
  for (const row of rows) {
    const previous = unique.get(row.interview_id);
    if (!previous || Date.parse(row.created) > Date.parse(previous.created))
      unique.set(row.interview_id, row);
  }
  for (const row of Array.from(unique.values())) {
    const id = row.conditionInstructionId;
    if (typeof id !== "number" || !available.has(id)) {
      unassigned.push(row);
      continue;
    }
    const group = grouped.get(id) ?? [];
    group.push(row);
    grouped.set(id, group);
  }
  const sorted = sortLectureItems(
    scenarios.map((s) => ({
      ...s,
      courseCategoryId: s.courseCategoryId ?? null,
    })),
  );
  const numbers = buildLectureNumberMap(sorted);
  const courses = sorted.map((scenario) => ({
    scenario,
    title:
      scenario.lectureTitle?.trim() ||
      `講座 ${numbers.get(scenario.id) ?? scenario.id}`,
    number: numbers.get(scenario.id),
    category:
      findCourseCategory(scenario.courseCategoryId ?? 0)?.name ?? "その他",
    ...summarizeCourseAttempts(grouped.get(scenario.id) ?? []),
  }));
  return {
    courses,
    unassigned: summarizeCourseAttempts(unassigned).attempts.reverse(),
  };
}

export type CourseScoreHistory = ReturnType<
  typeof buildCourseScoreHistory
>["courses"][number];
export function courseScoreStatus(course: CourseScoreHistory): string {
  if (course.completed) return "クリア済み";
  if (!course.attempts.length) return "未受講";
  if (course.full.length) return "練習中";
  return course.attempts.some((a) => getLearningScore(a)?.max === 50)
    ? "患者面接のみ"
    : "評価未完了";
}
